/**
 * ChatPanel
 * Pure view component for chat UI with draggable positioning, collapsible dock,
 * and customizable display preferences.
 * All chat message state managed by ChatContext.
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useChat } from '../contexts/ChatContext';
import { CHAT_CONFIG, getMessageColor, getMessageTypeLabel } from '../../../config/ui/chat.config';
import { HUD_COMPACT_LAYOUT, HUD_Z_INDEX } from '../../../config/ui/hudDefaults.config';
import { useRenderCount } from '../../diagnostics/useRenderCount';
import {
  ChatPrefs,
  ChatPosition,
  loadChatPrefs,
  saveChatPrefs,
  loadChatPosition,
  saveChatPosition,
  getFontSizePixels,
  clampChatPosition,
} from './chatPrefs';
import { ChatSettingsPopup } from './chat/ChatSettingsPopup';
import { chatPanelStyles as styles } from './chat/ChatPanel.styles';
import type { HudConcreteStyle } from './useHudTomeVibe';

// Re-export for backwards compatibility
export type { ChatMessage } from './ChatTypes';

interface ChatPanelProps {
  /**
   * Compact landscape peek width (W2a, 2026-09-28). When provided the panel
   * renders this width × HUD_COMPACT_LAYOUT.chat.height — a channel-tab row,
   * one message line, and the always-visible input row (older rows scroll).
   * Omitted → the accepted desktop surface (380×232), byte-for-byte.
   */
  compactWidth?: number;
  /** Active Tome HUD style direction ('a' | 'b' | 'c'). Undefined = classic mode. */
  hudStyle?: HudConcreteStyle;
}

/**
 * Memoised: everything it shows comes from ChatContext, so a HUD re-render
 * (the player frame updating mid-fight) must not re-render up to
 * `CHAT_CONFIG.maxMessages` rows. It still re-renders on every chat change.
 */
export const ChatPanel = React.memo(function ChatPanel({ compactWidth, hudStyle }: ChatPanelProps) {
  useRenderCount('ChatPanel');
  const {
    visibleMessages,
    filter,
    isFocused,
    isPaused,
    isCollapsed,
    sendMessage,
    setFilter,
    togglePause,
    setFocused,
    toggleCollapsed,
    clearMessages,
  } = useChat();

  const inputRef = useRef<HTMLInputElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [inputValue, setInputValue] = useState('');
  const [hovered, setHovered] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Preferences & Custom Position
  const [prefs, setPrefs] = useState<ChatPrefs>(() => loadChatPrefs());
  const [customPos, setCustomPos] = useState<ChatPosition | null>(() => loadChatPosition());

  // Unread message tracking while collapsed
  const lastReadCountRef = useRef(isCollapsed ? 0 : visibleMessages.length);
  const [unreadCount, setUnreadCount] = useState(isCollapsed ? visibleMessages.length : 0);

  useEffect(() => {
    if (isCollapsed) {
      const diff = Math.max(0, visibleMessages.length - lastReadCountRef.current);
      setUnreadCount(diff);
    } else {
      lastReadCountRef.current = visibleMessages.length;
      setUnreadCount(0);
    }
  }, [isCollapsed, visibleMessages.length]);

  const active = hovered || isFocused || showSettings || isDragging;

  // Auto-scroll to bottom when new messages arrive (unless paused)
  useEffect(() => {
    if (chatRef.current && !isPaused && !isCollapsed) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [visibleMessages, isPaused, isCollapsed]);

  // Focus input when isFocused changes — defer one tick so the panel has time
  // to expand from collapsed before the input element is in the DOM.
  useEffect(() => {
    if (isFocused && !isCollapsed) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      inputRef.current?.blur();
    }
  }, [isFocused, isCollapsed]);

  const handleSend = () => {
    if (inputValue.trim()) {
      sendMessage(inputValue.trim());
      setInputValue('');
    }
    // Blur input after send so Enter can open chat again
    setFocused(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSend();
    } else if (e.key === 'Escape') {
      // Escape leaves chat → blur so game input resumes immediately.
      setFocused(false);
      inputRef.current?.blur();
    }
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  const handleUpdatePrefs = useCallback((next: ChatPrefs) => {
    setPrefs(next);
    saveChatPrefs(next);
  }, []);

  const handleResetPosition = useCallback(() => {
    setCustomPos(null);
    saveChatPosition(null);
  }, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (compactWidth !== undefined) return;
    if (e.button !== 0) return;
    e.preventDefault();
    setIsDragging(true);

    const initialRect = containerRef.current?.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const originX = initialRect?.left ?? (customPos?.x ?? 16);
    const originY = initialRect?.top ?? (customPos?.y ?? (window.innerHeight - 248));

    const onPointerMove = (ev: PointerEvent) => {
      const deltaX = ev.clientX - startX;
      const deltaY = ev.clientY - startY;
      const clamped = clampChatPosition({ x: originX + deltaX, y: originY + deltaY }, 380, 232);
      setCustomPos(clamped);
    };

    const onPointerUp = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      setIsDragging(false);
      const deltaX = ev.clientX - startX;
      const deltaY = ev.clientY - startY;
      const clamped = clampChatPosition({ x: originX + deltaX, y: originY + deltaY }, 380, 232);
      setCustomPos(clamped);
      saveChatPosition(clamped);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  // ── Collapsed Dock Pill ───────────────────────────────────────────────────
  if (isCollapsed) {
    const collapsedFixedStyle: React.CSSProperties =
      customPos && compactWidth === undefined
        ? {
          position: 'fixed',
          left: `${customPos.x}px`,
          top: `${customPos.y}px`,
          right: 'auto',
          bottom: 'auto',
          zIndex: HUD_Z_INDEX.panels,
        }
        : {};

    return (
      <div
        data-testid="chat-panel-collapsed"
        data-hud-style={hudStyle}
        style={{
          ...styles.collapsedPill,
          ...(hudStyle
            ? {
              background: 'color-mix(in srgb, var(--tome-cover-lo, #1b261d) 90%, #000)',
              border: '1px solid var(--tome-hud-rule, rgba(216, 181, 106, 0.4))',
              borderRadius: 'var(--tome-cut-1, 6px)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.45)',
            }
            : {}),
          ...collapsedFixedStyle,
        }}
        onClick={toggleCollapsed}
        title="Click to open Chat (or press Enter)"
      >
        <span style={{ fontSize: '13px' }}>💬</span>
        <span style={{ ...styles.collapsedLabel, ...(hudStyle ? { color: 'var(--tome-hud-ink, #f5ebd2)' } : {}) }}>
          Chat
        </span>
        {unreadCount > 0 && (
          <span style={styles.unreadBadge} data-testid="chat-unread-badge">
            {unreadCount}
          </span>
        )}
      </div>
    );
  }

  // ── Expanded Chat Panel ───────────────────────────────────────────────────
  const containerPositionStyle: React.CSSProperties =
    customPos && compactWidth === undefined
      ? {
        position: 'fixed',
        left: `${customPos.x}px`,
        top: `${customPos.y}px`,
        right: 'auto',
        bottom: 'auto',
        zIndex: HUD_Z_INDEX.panels,
      }
      : {};

  return (
    <div
      ref={containerRef}
      className={hudStyle ? 'hudt-chat' : undefined}
      data-hud-style={hudStyle}
      style={{
        ...(compactWidth !== undefined
          ? { ...styles.container, width: compactWidth, height: HUD_COMPACT_LAYOUT.chat.height }
          : styles.container),
        ...(hudStyle
          ? {
            backgroundColor: 'transparent',
            border: 'none',
            boxShadow: 'none',
          }
          : {
            backgroundColor: `rgba(8, 10, 16, ${prefs.opacity})`,
          }),
        opacity: active ? 1 : 0.5,
        ...containerPositionStyle,
      }}
      data-testid="chat-panel-expanded"
      data-chat-count={visibleMessages.length}
      data-compact={compactWidth !== undefined ? 'true' : 'false'}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {hudStyle && <span className="hudt-ground" aria-hidden="true" />}
      {/* Channel tabs + window controls */}
      <div
        className={hudStyle ? 'hudt-chat-tabs' : undefined}
        style={{
          ...styles.tabBar,
          cursor: compactWidth !== undefined ? 'default' : isDragging ? 'grabbing' : 'grab',
        }}
        onPointerDown={handlePointerDown}
      >
        {compactWidth === undefined && (
          <span
            aria-hidden
            style={styles.dragGrip}
            title="Drag to reposition chat"
          >
            ⠿
          </span>
        )}

        {CHAT_CONFIG.filters.map(f => (
          <button
            key={f.id}
            role="tab"
            aria-selected={filter === f.id}
            onClick={(e) => {
              e.stopPropagation();
              setFilter(f.id);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            style={hudStyle ? { cursor: 'pointer' } : styles.tab(filter === f.id)}
          >
            {f.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {isPaused && <span style={styles.pausedBadge}>paused</span>}
        {active && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                togglePause();
              }}
              onPointerDown={(e) => e.stopPropagation()}
              style={styles.miniButton}
              title={isPaused ? 'Resume' : 'Pause'}
            >
              {isPaused ? '▶' : '⏸'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                clearMessages();
              }}
              onPointerDown={(e) => e.stopPropagation()}
              style={styles.miniButton}
              title="Clear"
            >
              🗑
            </button>
            <button
              data-testid="chat-settings-toggle"
              onClick={(e) => {
                e.stopPropagation();
                setShowSettings(prev => !prev);
              }}
              onPointerDown={(e) => e.stopPropagation()}
              style={styles.miniButton}
              title="Chat Settings"
            >
              ⚙
            </button>
            <button
              data-testid="chat-collapse-button"
              onClick={(e) => {
                e.stopPropagation();
                toggleCollapsed();
              }}
              onPointerDown={(e) => e.stopPropagation()}
              style={styles.miniButton}
              title="Minimize chat"
            >
              −
            </button>
          </>
        )}
      </div>

      {/* Messages */}
      <div
        ref={chatRef}
        className={hudStyle ? 'hudt-chat-log' : undefined}
        style={{
          ...styles.messages,
          ...(hudStyle
            ? {
              backgroundColor: `rgba(6, 8, 12, ${prefs.opacity})`,
              borderRadius: 4,
              color: '#e6e6ea',
            }
            : {}),
          fontSize: `${getFontSizePixels(prefs.fontSize)}px`,
        }}
      >
        {visibleMessages.map(msg => (
          <div key={msg.id} style={styles.message}>
            <span style={styles.typeLabel(getMessageColor(msg.type))}>
              {getMessageTypeLabel(msg.type)}
            </span>
            <span style={styles.sender(msg.type === 'system' ? '#f1c40f' : '#cfe3ff')}>
              {msg.sender}:
            </span>
            <span style={styles.text}>{msg.text}</span>
          </div>
        ))}
      </div>

      {/* Input — slim; shows a hint until focused (Enter to chat). */}
      <div
        className={hudStyle ? 'hudt-chat-input' : undefined}
        style={{
          ...styles.inputContainer,
          ...(hudStyle
            ? {
              backgroundColor: 'rgba(6, 8, 12, 0.72)',
              border: '1px solid var(--tome-hud-rule, rgba(216, 181, 106, 0.35))',
              borderRadius: 4,
            }
            : {}),
        }}
      >
        <input
          ref={inputRef}
          type="text"
          tabIndex={-1}
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={isFocused ? 'Say something…' : 'Press Enter to chat'}
          style={styles.input}
        />
      </div>

      {/* Settings Popup Modal */}
      {showSettings && (
        <ChatSettingsPopup
          prefs={prefs}
          onUpdatePrefs={handleUpdatePrefs}
          onResetPosition={handleResetPosition}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
});

